/**
 * ScadBlockToolbar — three floating toggles for ScadBlock.
 *
 * Two rendering modes:
 *   - "floating": small pill overlaid in the top-right corner of the viewer
 *   - "header":   slim bar at the top of the component; accepts an optional label
 */

import {
  Maximize,
  RotateCcw,
  Maximize2,
  Minimize2,
  ExternalLink,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

export interface ScadBlockToolbarProps {
  /** Rendering context: "floating" = overlay pill, "header" = slim top bar */
  mode: 'floating' | 'header';

  /** Optional label shown on the left in "header" mode */
  label?: string;

  /** Presentation mode (auto-rotation) state + handler */
  presentationMode: boolean;
  onPresentationModeToggle: (value: boolean) => void;

  /** Fit-to-bounds one-shot callback (null while viewer isn't ready) */
  onFitToBounds: (() => void) | null;

  /** Expand/collapse toggle — omit to hide the expand button (showcase mode) */
  isExpanded?: boolean;
  onExpandToggle?: () => void;

  /** Opens the current code in the full playground. Omit to hide the button. */
  onOpenInPlayground?: () => void;
}

// ---------------------------------------------------------------------------
// Shared icon button
// ---------------------------------------------------------------------------

interface IconButtonProps {
  onClick: () => void;
  label: string;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}

function IconButton({
  onClick,
  label,
  active = false,
  disabled = false,
  children,
}: IconButtonProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            className={[
              'flex h-7 w-7 items-center justify-center rounded transition-colors',
              disabled
                ? 'cursor-not-allowed opacity-20'
                : active
                  ? 'opacity-100 hover:opacity-80'
                  : 'opacity-40 hover:opacity-100',
            ].join(' ')}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="border-border bg-background text-foreground"
        >
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Toolbar buttons group (shared between both modes)
// ---------------------------------------------------------------------------

function ToolbarButtons({
  presentationMode,
  onPresentationModeToggle,
  onFitToBounds,
  isExpanded,
  onExpandToggle,
  onOpenInPlayground,
}: Omit<ScadBlockToolbarProps, 'mode' | 'label'>) {
  return (
    <>
      <IconButton
        onClick={() => onPresentationModeToggle(!presentationMode)}
        label={presentationMode ? 'Stop rotation' : 'Presentation mode'}
        active={presentationMode}
      >
        <RotateCcw className="h-3.5 w-3.5 text-foreground" />
      </IconButton>

      <IconButton
        onClick={() => onFitToBounds?.()}
        label="Fit to view"
        disabled={!onFitToBounds}
      >
        <Maximize className="h-3.5 w-3.5 text-foreground" />
      </IconButton>

      {onExpandToggle !== undefined && (
        <IconButton
          onClick={onExpandToggle}
          label={isExpanded ? 'Collapse' : 'Expand'}
          active={false}
        >
          {isExpanded ? (
            <Minimize2 className="h-3.5 w-3.5 text-foreground" />
          ) : (
            <Maximize2 className="h-3.5 w-3.5 text-foreground" />
          )}
        </IconButton>
      )}

      {onOpenInPlayground !== undefined && (
        <IconButton onClick={onOpenInPlayground} label="Open in playground">
          <ExternalLink className="h-3.5 w-3.5 text-foreground" />
        </IconButton>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function ScadBlockToolbar({
  mode,
  label,
  presentationMode,
  onPresentationModeToggle,
  onFitToBounds,
  isExpanded,
  onExpandToggle,
  onOpenInPlayground,
}: ScadBlockToolbarProps) {
  const buttons = (
    <ToolbarButtons
      presentationMode={presentationMode}
      onPresentationModeToggle={onPresentationModeToggle}
      onFitToBounds={onFitToBounds}
      isExpanded={isExpanded}
      onExpandToggle={onExpandToggle}
      onOpenInPlayground={onOpenInPlayground}
    />
  );

  if (mode === 'floating') {
    return (
      <div className="absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-lg bg-background/60 px-1.5 py-0.5 backdrop-blur-sm">
        {buttons}
      </div>
    );
  }

  // header mode
  return (
    <div className="flex h-10 flex-none items-center justify-between border-b border-border bg-background px-3">
      {label ? (
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-0.5">{buttons}</div>
    </div>
  );
}
